import { useMemo } from 'react'
import { RichTextDisplay } from '@/components/shared/RichTextDisplay'
import { cn } from '@/lib/utils'
import ReactMarkdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'

import {
  resolveHelpAppPath,
  rewriteHelpArticleHtmlLinks,
} from './help-app-path'
import { isRichTextHtml } from './help-content-format'

interface HelpArticleBodyProps {
  body: string
  className?: string
  orgSlug?: string
  placeholder?: string
}

export function HelpArticleBody({
  body,
  className,
  orgSlug,
  placeholder = 'Start writing the article body.',
}: HelpArticleBodyProps) {
  const isHtml = isRichTextHtml(body)
  const resolvedHtmlBody = useMemo(
    () => (isHtml ? rewriteHelpArticleHtmlLinks(body, orgSlug) : body),
    [body, isHtml, orgSlug],
  )

  if (isHtml) {
    return (
      <RichTextDisplay
        content={resolvedHtmlBody}
        showPlaceholder
        placeholder={placeholder}
        className={cn('text-base leading-7', className)}
      />
    )
  }

  return (
    <div
      className={cn(
        'prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-headings:tracking-normal prose-a:text-primary prose-a:underline-offset-2 prose-pre:rounded-lg prose-pre:border prose-pre:border-border prose-pre:bg-muted max-w-none break-words',
        className,
      )}
    >
      <ReactMarkdown
        rehypePlugins={[rehypeHighlight]}
        components={{
          a({ href, children, ...props }) {
            const isExternal = !!href && /^https?:\/\//i.test(href)
            const resolvedHref =
              href && !isExternal && href.startsWith('/')
                ? resolveHelpAppPath(orgSlug, href)
                : href

            return (
              <a
                href={resolvedHref}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
                {...props}
              >
                {children}
              </a>
            )
          },
        }}
      >
        {body || placeholder}
      </ReactMarkdown>
    </div>
  )
}
